<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppCard from '@/components/ui/AppCard.vue'
import Button from 'primevue/button'
import Tag from 'primevue/tag'
import Message from 'primevue/message'
import Skeleton from 'primevue/skeleton'
import ConfirmDialog from '@/components/ui/Dialog.vue'
import { useAuthStore } from '@/stores/auth'
import { idFromParam, kindFromRoute, useCatalogRecord } from '@/composables/useCatalogRecord'
import { removeCatalogItem } from '@/services/catalog'
import { formatCurrency, formatShortDate } from '@/utils/format'

/**
 * Detalle de un producto o un set.
 *
 * El borrado pide confirmación en un diálogo y solo entonces llama a `DELETE`.
 * Sin esa confirmación, un toque en un botón de 44px en móvil borra un registro
 * del catálogo del que puede haber cotizaciones: el borrado no tiene marcha
 * atrás en la interfaz.
 */

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const { record, loading, errorMessage, load } = useCatalogRecord()

const canManage = computed(() => authStore.hasPermission('catalog.manage'))
const confirmVisible = ref(false)
const deleting = ref(false)
const deleteError = ref<string | null>(null)

const id = computed(() => idFromParam(route.params.id))
/**
 * El tipo lo dice la ruta: `/catalog/sets/5` es un set sin mirar nada más. Antes
 * venía de `?kind=`, que había que propagar por todos los enlaces.
 */
const kindHint = computed(() => kindFromRoute(route.meta.catalogKind))

/** A qué pantalla del catálogo se vuelve: la del tipo que se está viendo. */
const kindPlural = computed(() => (kindHint.value === 'set' ? 'sets' : 'products'))

/**
 * Margen sobre el precio minorista.
 *
 * Se calcula con los strings del recurso y no con los ya formateados:
 * `formatCurrency` devuelve `"$ 1.234,56"`, y operar sobre eso da `NaN`.
 */
const marginLabel = computed(() => {
  if (record.value === null) return '—'
  const retail = Number.parseFloat(record.value.retail_price)
  const wholesale = Number.parseFloat(record.value.wholesale_price)
  if (!Number.isFinite(retail) || !Number.isFinite(wholesale) || retail <= 0) return '—'
  return `${(((retail - wholesale) / retail) * 100).toFixed(1)} %`
})

async function fetchRecord(): Promise<void> {
  deleteError.value = null
  if (id.value === null) {
    return
  }
  await load(id.value, kindHint.value)
}

onMounted(fetchRecord)

// Volver al listado y abrir otro registro cambia los params sin remontar la
// vista (misma ruta, distinto id), así que hay que recargar a mano.
watch([id, kindHint], fetchRecord)

function goBack(): void {
  void router.push({ name: `catalog.${kindPlural.value}.index` })
}

function goToEdit(): void {
  if (record.value === null) return
  void router.push({ name: `catalog.${record.value.kind === 'set' ? 'sets' : 'products'}.edit`, params: { id: String(record.value.id) } })
}

async function confirmDelete(): Promise<void> {
  if (record.value === null) return

  deleting.value = true
  deleteError.value = null

  try {
    await removeCatalogItem({ id: record.value.id, kind: record.value.kind })
    confirmVisible.value = false
    await router.push({ name: `catalog.${kindPlural.value}.index` })
  } catch (cause: unknown) {
    const maybeAxios = cause as { response?: { data?: { error?: { message?: string } } } }
    deleteError.value = maybeAxios.response?.data?.error?.message ?? 'No se pudo borrar el registro.'
  } finally {
    deleting.value = false
  }
}
</script>

<template>
  <div class="space-y-6 animate-fade-in">
    <header class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div class="min-w-0">
        <p class="text-sm text-surface-500 dark:text-surface-400">
          <button
            type="button"
            class="hover:underline"
            @click="goBack"
          >
            Catálogo
          </button>
          <span aria-hidden="true"> / </span>
          <span class="font-mono">{{ route.params.id }}</span>
        </p>
        <!--
          El nombre se parte en varias lineas. Es el titulo de la pagina: recortado
          aqui, el nombre largo del producto quedaba invisible justo donde se va a
          mirarlo.
        -->
        <h1 class="mt-1 break-words text-2xl font-semibold text-surface-900 dark:text-surface-100">
          {{ record?.name ?? 'Detalle del registro' }}
        </h1>
      </div>
      <div class="flex flex-wrap gap-2">
        <Button
          label="Volver"
          icon="pi pi-arrow-left"
          severity="secondary"
          @click="goBack"
        />
        <Button
          v-if="canManage && record"
          label="Editar"
          icon="pi pi-pencil"
          @click="goToEdit"
        />
        <Button
          v-if="canManage && record"
          label="Borrar"
          icon="pi pi-trash"
          severity="danger"
          outlined
          @click="confirmVisible = true"
        />
      </div>
    </header>

    <Message
      v-if="errorMessage"
      severity="error"
      :closable="false"
      variant="simple"
      role="alert"
    >
      {{ errorMessage }}
    </Message>

    <Message
      v-if="deleteError"
      severity="error"
      :closable="false"
      variant="simple"
      role="alert"
    >
      {{ deleteError }}
    </Message>

    <AppCard v-if="loading">
      <div class="space-y-4">
        <Skeleton
          v-for="index in 4"
          :key="index"
          height="1.25rem"
        />
      </div>
    </AppCard>

    <AppCard v-else-if="record">
      <template #header>
        <div class="flex flex-wrap items-center gap-2">
          <h2 class="text-lg font-semibold text-surface-900 dark:text-surface-100">
            {{ record.kind === 'set' ? 'Set' : 'Producto' }}
          </h2>
          <Tag
            :value="record.kind === 'set' ? 'Set' : 'Producto'"
            :severity="record.kind === 'set' ? 'info' : 'secondary'"
          />
          <Tag
            v-if="record.type"
            :value="record.type"
            severity="success"
          />
        </div>
      </template>

      <dl class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <dt class="text-sm text-surface-500 dark:text-surface-400">
            Referencia
          </dt>
          <dd class="font-mono text-surface-900 dark:text-surface-100">
            {{ record.reference }}
          </dd>
        </div>
        <div>
          <dt class="text-sm text-surface-500 dark:text-surface-400">
            Nombre
          </dt>
          <dd class="text-surface-900 dark:text-surface-100">
            {{ record.name }}
          </dd>
        </div>
        <div v-if="record.kind === 'set'">
          <dt class="text-sm text-surface-500 dark:text-surface-400">
            Tipo
          </dt>
          <dd class="text-surface-900 dark:text-surface-100">
            {{ record.type }}
          </dd>
        </div>
        <div>
          <dt class="text-sm text-surface-500 dark:text-surface-400">
            Precio minorista
          </dt>
          <dd class="text-lg font-semibold text-surface-900 dark:text-surface-100">
            {{ formatCurrency(record.retail_price) }}
          </dd>
        </div>
        <div>
          <dt class="text-sm text-surface-500 dark:text-surface-400">
            Precio mayorista
          </dt>
          <dd class="text-lg font-semibold text-surface-900 dark:text-surface-100">
            {{ formatCurrency(record.wholesale_price) }}
          </dd>
        </div>
        <div>
          <dt class="text-sm text-surface-500 dark:text-surface-400">
            Margen bruto
          </dt>
          <dd class="text-surface-900 dark:text-surface-100">
            {{ marginLabel }}
          </dd>
        </div>
        <div>
          <dt class="text-sm text-surface-500 dark:text-surface-400">
            Creado
          </dt>
          <dd class="text-surface-900 dark:text-surface-100">
            {{ formatShortDate(record.created_at) }}
          </dd>
        </div>
        <div>
          <dt class="text-sm text-surface-500 dark:text-surface-400">
            Actualizado
          </dt>
          <dd class="text-surface-900 dark:text-surface-100">
            {{ formatShortDate(record.updated_at) }}
          </dd>
        </div>
      </dl>
    </AppCard>

    <AppCard v-else-if="!errorMessage">
      <div class="py-12 text-center">
        <p class="text-surface-500 dark:text-surface-400">
          No hay nada que mostrar.
        </p>
      </div>
    </AppCard>

    <ConfirmDialog
      v-model:visible="confirmVisible"
      title="Borrar registro"
      :confirm-loading="deleting"
      @confirm="confirmDelete"
      @cancel="confirmVisible = false"
    >
      <p class="text-surface-700 dark:text-surface-300">
        Se va a borrar
        <strong>{{ record?.reference }}</strong> del catálogo. Esta acción no se puede deshacer.
      </p>
    </ConfirmDialog>
  </div>
</template>
